import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { EMOJIS, COLORS } from "@/lib/helpers";
import { Plus, Trash2, Edit3, Download, RefreshCw, Lock, Save, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

export default function Settings() {
  const { family, members, refresh, lock, setFamily } = useApp();
  const [addOpen, setAddOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const [draftName, setDraftName] = useState("");
  const [draftEmoji, setDraftEmoji] = useState(EMOJIS[0]);
  const [draftColor, setDraftColor] = useState(COLORS[0]);

  const [showReset, setShowReset] = useState(false);
  const [showResetConfirm2, setShowResetConfirm2] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [newPinConfirm, setNewPinConfirm] = useState("");

  const [compName, setCompName] = useState(family.comp_name);
  const [start, setStart] = useState(family.tournament_start);
  const [end, setEnd] = useState(family.tournament_end);

  const openAdd = () => {
    setDraftName("");
    setDraftEmoji(EMOJIS[members.length % EMOJIS.length]);
    setDraftColor(COLORS[members.length % COLORS.length]);
    setAddOpen(true);
  };

  const saveNew = async () => {
    if (!draftName.trim()) {
      toast.error("Name required.");
      return;
    }
    await api.addMember({ name: draftName.trim(), emoji: draftEmoji, color: draftColor });
    setAddOpen(false);
    await refresh();
    toast(`${draftName} added to the roster.`);
  };

  const openEdit = (m) => {
    setEditMember(m);
    setDraftName(m.name);
    setDraftEmoji(m.emoji);
    setDraftColor(m.color);
  };

  const saveEdit = async () => {
    await api.updateMember(editMember.id, {
      name: draftName.trim(),
      emoji: draftEmoji,
      color: draftColor,
    });
    setEditMember(null);
    await refresh();
    toast("Updated.");
  };

  const removeMember = async (m) => {
    if (members.length <= 2) {
      toast.error("Need at least 2 anglers.");
      return;
    }
    await api.deleteMember(m.id);
    await refresh();
    toast(`${m.name} removed from the roster.`);
  };

  const saveCompMeta = async () => {
    await api.updateFamily({
      comp_name: compName,
      tournament_start: start,
      tournament_end: end,
    });
    setFamily({ ...family, comp_name: compName, tournament_start: start, tournament_end: end });
    toast("Saved.");
  };

  const doReset = async () => {
    await api.resetComp();
    setShowResetConfirm2(false);
    await refresh();
    toast("Fresh comp. Board wiped.");
  };

  const doChangePin = async () => {
    if (newPin.length !== 4 || newPin !== newPinConfirm) {
      toast.error("PINs don't match.");
      return;
    }
    await api.changePin(newPin);
    setShowChangePin(false);
    setNewPin("");
    setNewPinConfirm("");
    toast("PIN changed.");
  };

  const downloadCsv = () => {
    window.location.href = api.exportCsvUrl();
  };

  return (
    <div className="px-5 pt-4 pb-4 space-y-6">
      <h1 className="font-display text-3xl font-black text-[#0A3D62]">Settings</h1>

      {/* Comp meta */}
      <section className="bg-white rounded-3xl p-5 border border-[#0A3D62]/5 space-y-4">
        <h2 className="font-display text-lg font-black text-[#0A3D62]">Comp details</h2>
        <div>
          <label className="text-xs uppercase font-bold text-[#556B7D]">Name</label>
          <input
            data-testid="settings-comp-name"
            value={compName}
            onChange={(e) => setCompName(e.target.value)}
            className="w-full mt-1 text-lg font-semibold text-[#0A3D62] bg-[#FAF8F3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#E8A838]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase font-bold text-[#556B7D]">Start</label>
            <input
              type="date"
              data-testid="settings-start-date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full mt-1 text-base font-semibold text-[#0A3D62] bg-[#FAF8F3] rounded-xl px-3 py-3 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-bold text-[#556B7D]">End</label>
            <input
              type="date"
              data-testid="settings-end-date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full mt-1 text-base font-semibold text-[#0A3D62] bg-[#FAF8F3] rounded-xl px-3 py-3 focus:outline-none"
            />
          </div>
        </div>
        <button
          onClick={saveCompMeta}
          data-testid="settings-save-meta"
          className="w-full py-3 rounded-2xl bg-[#0A3D62] text-white font-bold active:scale-[0.98]"
        >
          Save comp details
        </button>
      </section>

      {/* Members */}
      <section className="bg-white rounded-3xl p-5 border border-[#0A3D62]/5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-black text-[#0A3D62]">Anglers</h2>
          <button
            onClick={openAdd}
            data-testid="settings-add-member-btn"
            className="w-10 h-10 rounded-full bg-[#E8A838] text-[#1A2A35] flex items-center justify-center active:scale-95"
          >
            <Plus className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <div className="space-y-2">
          {members.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-3 rounded-2xl p-2 hover:bg-[#FAF8F3]"
              data-testid={`settings-member-${m.id}`}
            >
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                style={{ background: m.color + "33" }}
              >
                {m.emoji}
              </div>
              <div className="flex-1 font-semibold text-[#0A3D62]">{m.name}</div>
              <button
                onClick={() => openEdit(m)}
                className="w-9 h-9 rounded-full bg-[#0A3D62]/10 text-[#0A3D62] flex items-center justify-center active:scale-95"
                data-testid={`settings-edit-member-${m.id}`}
              >
                <Edit3 className="w-4 h-4" strokeWidth={2.5} />
              </button>
              <button
                onClick={() => removeMember(m)}
                className="w-9 h-9 rounded-full bg-[#D9381E]/10 text-[#D9381E] flex items-center justify-center active:scale-95"
                data-testid={`settings-delete-member-${m.id}`}
              >
                <Trash2 className="w-4 h-4" strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Security & data */}
      <section className="bg-white rounded-3xl p-5 border border-[#0A3D62]/5 space-y-2">
        <h2 className="font-display text-lg font-black text-[#0A3D62] mb-2">Security & data</h2>
        <button
          onClick={() => setShowChangePin(true)}
          data-testid="settings-change-pin-btn"
          className="w-full flex items-center gap-3 py-3 px-3 rounded-2xl hover:bg-[#FAF8F3] active:scale-[0.99] text-left"
        >
          <div className="w-10 h-10 rounded-full bg-[#0A3D62]/10 text-[#0A3D62] flex items-center justify-center">
            <Lock className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#0A3D62]">Change PIN</div>
            <div className="text-xs text-[#556B7D]">Set a new 4-digit family PIN</div>
          </div>
        </button>
        <button
          onClick={lock}
          data-testid="settings-lock-btn"
          className="w-full flex items-center gap-3 py-3 px-3 rounded-2xl hover:bg-[#FAF8F3] active:scale-[0.99] text-left"
        >
          <div className="w-10 h-10 rounded-full bg-[#0A3D62]/10 text-[#0A3D62] flex items-center justify-center">
            <Lock className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#0A3D62]">Lock now</div>
            <div className="text-xs text-[#556B7D]">Force PIN re-entry</div>
          </div>
        </button>
        <button
          onClick={downloadCsv}
          data-testid="settings-export-btn"
          className="w-full flex items-center gap-3 py-3 px-3 rounded-2xl hover:bg-[#FAF8F3] active:scale-[0.99] text-left"
        >
          <div className="w-10 h-10 rounded-full bg-[#2D6A4F]/15 text-[#2D6A4F] flex items-center justify-center">
            <Download className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#0A3D62]">Export catches (CSV)</div>
            <div className="text-xs text-[#556B7D]">For posterity, and arguments</div>
          </div>
        </button>
        <button
          onClick={() => setShowReset(true)}
          data-testid="settings-reset-btn"
          className="w-full flex items-center gap-3 py-3 px-3 rounded-2xl hover:bg-[#D9381E]/5 active:scale-[0.99] text-left"
        >
          <div className="w-10 h-10 rounded-full bg-[#D9381E]/10 text-[#D9381E] flex items-center justify-center">
            <RefreshCw className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="flex-1">
            <div className="font-bold text-[#D9381E]">Reset comp</div>
            <div className="text-xs text-[#556B7D]">Wipe every catch, start fresh</div>
          </div>
        </button>
      </section>

      {/* Add member drawer */}
      <MemberDrawer
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Add angler"
        name={draftName}
        emoji={draftEmoji}
        color={draftColor}
        onNameChange={setDraftName}
        onEmojiChange={setDraftEmoji}
        onColorChange={setDraftColor}
        onSave={saveNew}
        testPrefix="add-member"
      />

      {/* Edit member drawer */}
      <MemberDrawer
        open={!!editMember}
        onOpenChange={(v) => !v && setEditMember(null)}
        title={`Edit ${editMember?.name || ""}`}
        name={draftName}
        emoji={draftEmoji}
        color={draftColor}
        onNameChange={setDraftName}
        onEmojiChange={setDraftEmoji}
        onColorChange={setDraftColor}
        onSave={saveEdit}
        testPrefix="edit-member"
      />

      {/* Change PIN drawer */}
      <Drawer open={showChangePin} onOpenChange={setShowChangePin}>
        <DrawerContent className="bg-[#FAF8F3]">
          <DrawerHeader>
            <DrawerTitle className="font-display text-2xl text-[#0A3D62]">Change PIN</DrawerTitle>
          </DrawerHeader>
          <div className="px-5 pb-6 space-y-4">
            <input
              type="tel"
              inputMode="numeric"
              maxLength={4}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="New PIN"
              data-testid="settings-new-pin"
              className="w-full text-3xl tracking-[0.6em] font-black text-center text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl py-5 focus:outline-none focus:border-[#E8A838]"
            />
            <input
              type="tel"
              inputMode="numeric"
              maxLength={4}
              value={newPinConfirm}
              onChange={(e) => setNewPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="Confirm"
              data-testid="settings-new-pin-confirm"
              className="w-full text-3xl tracking-[0.6em] font-black text-center text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl py-5 focus:outline-none focus:border-[#E8A838]"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowChangePin(false)}
                className="flex-1 py-4 rounded-2xl bg-white border border-[#0A3D62]/10 text-[#0A3D62] font-bold"
                data-testid="settings-change-pin-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doChangePin}
                className="flex-1 py-4 rounded-2xl bg-[#E8A838] text-[#1A2A35] font-black"
                data-testid="settings-change-pin-save"
              >
                Save
              </button>
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Reset confirm 1 */}
      <AlertDialog open={showReset} onOpenChange={setShowReset}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Reset the comp?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#556B7D]">
              This wipes every catch and starts fresh for next Easter. Sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-reset-cancel-1">Nope</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowReset(false);
                setShowResetConfirm2(true);
              }}
              data-testid="settings-reset-confirm-1"
              className="bg-[#D9381E] hover:bg-[#D9381E]/90"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset confirm 2 */}
      <AlertDialog open={showResetConfirm2} onOpenChange={setShowResetConfirm2}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Really really sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#556B7D]">
              Every catch will be gone forever. Anglers stay. There is no undo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="settings-reset-cancel-2">Keep the history</AlertDialogCancel>
            <AlertDialogAction
              onClick={doReset}
              data-testid="settings-reset-confirm-2"
              className="bg-[#D9381E] hover:bg-[#D9381E]/90"
            >
              Wipe it all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function MemberDrawer({
  open,
  onOpenChange,
  title,
  name,
  emoji,
  color,
  onNameChange,
  onEmojiChange,
  onColorChange,
  onSave,
  testPrefix,
}) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-[#FAF8F3] max-h-[90vh]">
        <DrawerHeader>
          <DrawerTitle className="font-display text-2xl text-[#0A3D62]">{title}</DrawerTitle>
        </DrawerHeader>
        <div className="px-5 pb-6 space-y-5 overflow-y-auto">
          <input
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Angler's name"
            data-testid={`${testPrefix}-name-input`}
            className="w-full text-xl font-semibold text-[#0A3D62] bg-white rounded-xl px-4 py-3 border border-[#0A3D62]/10 focus:outline-none focus:ring-2 focus:ring-[#E8A838]"
          />
          <div>
            <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">Avatar</div>
            <div className="flex flex-wrap gap-2">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => onEmojiChange(e)}
                  className={`w-11 h-11 rounded-xl text-2xl flex items-center justify-center active:scale-90 ${
                    emoji === e ? "bg-[#E8A838] scale-110" : "bg-white"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase font-bold text-[#556B7D] mb-2">Colour</div>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => onColorChange(c)}
                  className="w-9 h-9 rounded-full"
                  style={{
                    background: c,
                    outline: color === c ? "3px solid #1A2A35" : "none",
                    outlineOffset: "2px",
                  }}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 py-4 rounded-2xl bg-white border border-[#0A3D62]/10 text-[#0A3D62] font-bold flex items-center justify-center gap-2"
              data-testid={`${testPrefix}-cancel`}
            >
              <X className="w-5 h-5" /> Cancel
            </button>
            <button
              onClick={onSave}
              className="flex-1 py-4 rounded-2xl bg-[#E8A838] text-[#1A2A35] font-black flex items-center justify-center gap-2"
              data-testid={`${testPrefix}-save`}
            >
              <Save className="w-5 h-5" /> Save
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
